<template>
  <div class="report-stock-tables">
    <details v-for="(group, groupIndex) in groups" :key="groupIndex" class="stock-group" open>
      <summary class="stock-group-toggle">
        <span>{{ group.title }}<small class="stock-group-count">{{ group.rows.length }}只</small></span>
        <span class="collapse-label">收起</span><span class="expand-label">展开</span>
      </summary>
      <p v-if="group.notes" class="stock-group-notes">{{ group.notes }}</p>
      <div v-if="group.rows.length" class="stock-table-scroll" role="region" :aria-label="`${group.title}行情表格，可横向滚动`" tabindex="0">
        <table class="report-stock-table">
          <caption class="sr-only">{{ group.title }}</caption>
          <thead><tr>
            <th scope="col">股票名称(代码)</th>
            <th scope="col" :aria-sort="sortOrder(groupIndex, 'previous')"><button type="button" class="sort-button" @click="toggleSort(groupIndex, 'previous')">昨日涨幅 <span aria-hidden="true">{{ sortIndicator(groupIndex, 'previous') }}</span></button><small v-if="previousDate">{{ previousDate }}</small></th>
            <th scope="col" :aria-sort="sortOrder(groupIndex, 'today')"><button type="button" class="sort-button" @click="toggleSort(groupIndex, 'today')">今日涨幅 <span aria-hidden="true">{{ sortIndicator(groupIndex, 'today') }}</span></button></th>
            <th scope="col">概念说明</th>
          </tr></thead>
          <tbody>
            <tr v-for="(row, index) in sortedRows(group, groupIndex)" :key="`${row.code}-${index}`">
              <td><button type="button" class="stock-link stock-identity" @click="$emit('select', row.code, row.name)">{{ row.name }}({{ row.code }})</button></td>
              <td class="stock-percent" :class="performanceClass(previousPct(row))" :title="previousIsOriginal(row) ? '暂无上一交易日日线，保留报告原涨幅' : '上一交易日日线涨幅'">{{ formatPercent(previousPct(row)) }}<small v-if="previousIsOriginal(row)">原文</small></td>
              <td class="stock-percent" :class="performanceClass(todayPct(row))" :title="quoteLabel">{{ formatPercent(todayPct(row)) }}</td>
              <td class="stock-description">{{ row.description || '--' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </details>
  </div>
</template>

<script>
import { finitePercent, formatPercent, performanceClass } from '@/utils/reportMarket'

export default {
  name: 'ReportStockTables',
  props: {
    groups: { type: Array, default: () => [] },
    quotes: { type: Object, default: () => ({}) },
    previousQuotes: { type: Object, default: () => ({}) },
    previousDate: { type: String, default: '' },
    quoteLabel: { type: String, default: '' }
  },
  data() {
    return { groupSorts: {} }
  },
  methods: {
    formatPercent, performanceClass,
    toggleSort(index, field) {
      const current = this.groupSorts[index]
      this.$set(this.groupSorts, index, { field, direction: current && current.field === field && current.direction === 'desc' ? 'asc' : 'desc' })
    },
    sortOrder(index, field) {
      const sort = this.groupSorts[index]
      return !sort || sort.field !== field ? 'none' : sort.direction === 'desc' ? 'descending' : 'ascending'
    },
    sortIndicator(index, field) {
      const order = this.sortOrder(index, field)
      return order === 'none' ? '↕' : order === 'descending' ? '↓' : '↑'
    },
    sortedRows(group, index) {
      const sort = this.groupSorts[index]
      if (!sort) return group.rows
      const value = sort.field === 'today' ? this.todayPct : this.previousPct
      return group.rows.map((row, position) => ({ row, position })).sort((a, b) => {
        const left = value(a.row)
        const right = value(b.row)
        if (left === null && right === null) return a.position - b.position
        if (left === null) return 1
        if (right === null) return -1
        return (sort.direction === 'desc' ? right - left : left - right) || a.position - b.position
      }).map(item => item.row)
    },
    todayPct(row) { return this.quotes[row.code] ? finitePercent(this.quotes[row.code].pct) : null },
    previousIsOriginal(row) { return (!this.previousQuotes[row.code] || finitePercent(this.previousQuotes[row.code].pct) === null) && row.originalPct !== null },
    previousPct(row) { return this.previousIsOriginal(row) ? row.originalPct : this.previousQuotes[row.code] ? finitePercent(this.previousQuotes[row.code].pct) : null }
  }
}
</script>

<style scoped>
.stock-group { margin: 12px 0; }
.stock-group-toggle { display: flex; align-items: center; gap: 8px; padding: 8px 10px; cursor: pointer; list-style: none; background: var(--bg-muted); color: var(--text-main); border: 1px solid var(--border-color); border-radius: 6px; font-size: 0.85rem; font-weight: bold; }
.stock-group-toggle::-webkit-details-marker { display: none; }
.stock-group-toggle::before { content: '▸'; }
.stock-group[open] > .stock-group-toggle::before { transform: rotate(90deg); }
.stock-group-count { margin-left: 8px; color: var(--text-muted); font-weight: normal; }
.collapse-label, .expand-label { margin-left: auto; color: var(--accent-color); font-weight: normal; font-size: 0.75rem; }
.stock-group:not([open]) .collapse-label, .stock-group[open] .expand-label { display: none; }
.stock-group[open] > .stock-group-toggle { margin-bottom: 6px; }
.stock-group-notes { color: var(--text-main); font-size: 0.85rem; white-space: pre-line; }
.stock-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid var(--border-color); border-radius: 6px; }
.report-stock-table { width: 100%; min-width: 600px; border-collapse: collapse; background: var(--bg-card); color: var(--text-main); font-size: 0.85rem; }
.report-stock-table caption { caption-side: top; padding: 8px 10px; color: var(--text-main); font-weight: bold; background: var(--bg-muted); }
.report-stock-table th, .report-stock-table td { padding: 8px 10px; border-top: 1px solid var(--border-color); vertical-align: top; }
.report-stock-table th { white-space: nowrap; font-size: 0.8rem; }
.report-stock-table small { display: block; font-size: 0.65rem; font-weight: normal; color: var(--text-muted); }
.stock-link { border: 0; padding: 0; background: transparent; color: var(--accent-color); text-align: left; cursor: pointer; }
.stock-link:hover, .stock-link:focus-visible { text-decoration: underline; }
.stock-identity, .stock-percent { white-space: nowrap; font-variant-numeric: tabular-nums; }
.sort-button { padding: 0; border: 0; background: transparent; color: var(--text-main); font: inherit; cursor: pointer; }
.sort-button:hover, .sort-button:focus-visible { color: var(--accent-color); text-decoration: underline; }
.stock-percent { font-weight: 600; }
.stock-description { min-width: 200px; white-space: pre-line; overflow-wrap: anywhere; }
.performance-up { color: var(--color-up-color); }
.performance-down { color: var(--color-down-color); }
.performance-neutral { color: var(--text-muted); }
</style>
